import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  getMainMenuExternalUrl,
  MAIN_MENU_EXTERNAL_LINKS,
} from 'constants/mainMenuExternalLinks';
import type { MainMenuSidebarMenuItem } from '../mainMenuSidebarMenus';
import { getSidebarMenuPosition } from './getSidebarMenuPosition';

import * as S from './styled';

type PanelPosition = {
  top: number;
  left: number;
};

interface MainMenuSidebarMenuProps {
  panelId: string;
  titleKey: string;
  items: MainMenuSidebarMenuItem[];
  trigger: ReactNode;
  triggerAriaLabel: string;
}

const ESTIMATED_PANEL_HEIGHT: Record<string, number> = {
  'help-support-menu-panel': 300,
  'about-gumb-menu-panel': 260,
};

const MainMenuSidebarMenu: FC<MainMenuSidebarMenuProps> = ({
  panelId,
  titleKey,
  items,
  trigger,
  triggerAriaLabel,
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelPosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect() ?? null;

    setPanelPosition(
      getSidebarMenuPosition({
        triggerRect,
        panelRect,
        estimatedPanelHeight: ESTIMATED_PANEL_HEIGHT[panelId],
      }),
    );
  }, [panelId]);

  const toggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setExpanded((prev) => {
        const next = !prev;
        if (next) {
          updatePanelPosition();
        }
        return next;
      });
    },
    [updatePanelPosition],
  );

  const close = useCallback(() => setExpanded(false), []);

  useLayoutEffect(() => {
    if (!expanded) return;
    updatePanelPosition();
  }, [expanded, updatePanelPosition, items.length]);

  useEffect(() => {
    if (!expanded) return undefined;

    const onResizeOrScroll = () => updatePanelPosition();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);

    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
    };
  }, [expanded, updatePanelPosition]);

  useEffect(() => {
    if (!expanded) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      const portalRoot = document.getElementById(panelId);
      if (portalRoot?.contains(target)) return;
      close();
    };

    const timerId = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [close, expanded, panelId]);

  const expandedPanel =
    expanded && panelPosition
      ? createPortal(
          <S.StyledPanelAnchor
            id={panelId}
            style={{
              top: panelPosition.top,
              left: panelPosition.left,
            }}
          >
            <S.StyledPanel ref={panelRef} role="menu" aria-label={t(titleKey)}>
              <S.StyledPanelTitle>{t(titleKey)}</S.StyledPanelTitle>
              <S.StyledPanelDivider />
              <S.StyledMenuList>
                {items.map(({ labelKey, linkKey, emoji }) => {
                  const href = getMainMenuExternalUrl(
                    i18n.language,
                    MAIN_MENU_EXTERNAL_LINKS[linkKey],
                  );

                  return (
                    <li key={labelKey} role="none">
                      <S.StyledMenuLink
                        role="menuitem"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={close}
                      >
                        <S.StyledMenuEmoji aria-hidden>
                          {emoji}
                        </S.StyledMenuEmoji>
                        <S.StyledMenuLabel>{t(labelKey)}</S.StyledMenuLabel>
                      </S.StyledMenuLink>
                    </li>
                  );
                })}
              </S.StyledMenuList>
            </S.StyledPanel>
          </S.StyledPanelAnchor>,
          document.body,
        )
      : null;

  return (
    <S.StyledMenuRoot ref={rootRef}>
      <S.StyledMenuTrigger
        ref={triggerRef}
        type="button"
        $expanded={expanded}
        aria-expanded={expanded}
        aria-haspopup="menu"
        aria-label={triggerAriaLabel}
        onClick={toggle}
      >
        {trigger}
      </S.StyledMenuTrigger>
      {expandedPanel}
    </S.StyledMenuRoot>
  );
};

export default MainMenuSidebarMenu;
